import { useContext, useState } from "react";
import { DrawingContext } from "../../../../components/drawing-tool/DrawingTool";
import { AccordionTypes } from "../../../../helpers/AccordionHelper";
import { findIncomingIngredientsAndProcess } from "../../../../helpers/DrawingHelper";
import { errorView } from "../../../../helpers/ErrorHandler";

interface RecipeData {
  ingredientList: any[];
  processList: any[];
  processId: string;
}

interface UseRecipeDataProps {
  processId: string;
  nodeItem: any;
  processRecipeData: any;
  accordionType: AccordionTypes | null;
}

/**
 * Custom hook for managing recipe data loading and state
 * Follows Single Responsibility Principle - handles only recipe data logic
 */
export const useRecipeData = ({
  processId,
  nodeItem,
  processRecipeData,
  accordionType
}: UseRecipeDataProps) => {
  const { chartEdges, chartNodes } = useContext(DrawingContext);
  const [recipeData, setRecipeData] = useState<RecipeData>({
    ingredientList: [],
    processList: [],
    processId: ""
  });

  const loadRecipeData = (): boolean => {
    let processList: any[] = [];
    let ingredientList: any[] = [];

    try {
      const incomings = findIncomingIngredientsAndProcess(
        chartEdges,
        chartNodes,
        nodeItem.id,
        ingredientList,
        processList
      );

      const processIncomings = incomings?.processList?.length || 0;
      const ingredientIncomings = incomings?.ingredientList?.length || 0;

      if (incomings.ingredientList.length > 0 || incomings.processList.length > 0) {
        let notSavedProcessCount = 0;
        let notSavedPreviousComponent = 0;

        processList = incomings?.processList
          ?.map((item: any) => {
            const processMap = processRecipeData?.processList?.find(
              (process: any) => item?.data?.reference?.processId === process.id
            );

            if (!item?.data?.reference?.processId) {
              notSavedProcessCount++;
            }
            if (item.previousComponentId === null) {
              notSavedPreviousComponent++;
            }
            if (processIncomings === 1) {
              item.value = 100;
            }

            if (processMap) {
              return { ...processMap, ...item };
            }
            return item;
          })
          .filter((item: any) => item?.data?.reference?.processId !== processId);

        let notSavedIngredientCount = 0;
        ingredientList = incomings?.ingredientList
          .map((item: any) => {
            const ingredientMap = processRecipeData?.ingredientList?.find(
              (ingredient: any) =>
                item?.data?.reference?.ingredientId === ingredient.id &&
                item?.data?.reference?.supplierId === ingredient.supplierId
            );

            let supplierName = "";
            if (!item?.data?.reference?.supplierId) {
              notSavedIngredientCount++;
            }
            if (item.previousComponentId === null) {
              notSavedPreviousComponent++;
            }
            if (ingredientIncomings === 1) {
              item.value = 100;
            }

            if (ingredientMap) {
              return { supplierName, ...ingredientMap, ...item };
            }
            return { ...item, supplierName };
          })
          .filter(
            (v: any, i: any, a: any) =>
              a.findIndex(
                (t: any) =>
                  t.data?.reference?.ingredientId === v.data?.reference?.ingredientId &&
                  t?.data?.reference?.supplierId === v?.data?.reference?.supplierId
              ) === i
          );

        // Validation checks
        if (notSavedIngredientCount > 0) {
          errorView("Please save ingredient for this product");
          return false;
        }
        if (notSavedProcessCount > 0) {
          errorView("Please save process for this product");
          return false;
        }
        if (notSavedPreviousComponent > 0) {
          errorView("Please save previous process for this recipe");
          return false;
        }

        setRecipeData({ ingredientList, processList, processId });
        return true;
      } else {
        errorView("Please create ingredient or process");
        return false;
      }
    } catch (error) {
      errorView("Something went wrong");
      return false;
    }
  };

  return { recipeData, loadRecipeData };
};
